"use client";

import { useState, useEffect, useCallback } from "react";

export interface SearchHistoryItem {
  id: string;
  title: string;
  type: string;
  url: string;
  subtitle?: string;
  timestamp: number;
  searchQuery?: string;
}

interface UseSearchHistoryReturn {
  history: SearchHistoryItem[];
  addToHistory: (item: Omit<SearchHistoryItem, 'timestamp'>) => void;
  removeFromHistory: (id: string) => void;
  clearHistory: () => void;
  getRecentHistory: (limit?: number) => SearchHistoryItem[];
}

const STORAGE_KEY = "search_click_history";
const MAX_HISTORY_ITEMS = 50;

export function useSearchHistory(): UseSearchHistoryReturn {
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsedHistory = JSON.parse(stored);
        // Filter out items older than 30 days
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const recentHistory = parsedHistory.filter((item: SearchHistoryItem) => 
          item.timestamp > thirtyDaysAgo
        );
        setHistory(recentHistory);
        
        // Update localStorage with filtered history
        if (recentHistory.length !== parsedHistory.length) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(recentHistory));
        }
      } catch (error) {
        console.warn("Failed to load search history:", error);
        setHistory([]);
      }
    }
  }, []);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [history]);

  const addToHistory = useCallback((item: Omit<SearchHistoryItem, 'timestamp'>) => {
    const historyItem: SearchHistoryItem = {
      ...item,
      timestamp: Date.now()
    };

    setHistory(prev => {
      // Remove any existing item with the same ID
      const filtered = prev.filter(existing => existing.id !== item.id);
      
      // Add new item at the beginning
      const updated = [historyItem, ...filtered];
      
      // Limit to max items
      return updated.slice(0, MAX_HISTORY_ITEMS);
    });
  }, []);

  const removeFromHistory = useCallback((id: string) => {
    setHistory(prev => prev.filter(item => item.id !== id));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  const getRecentHistory = useCallback((limit: number = 10) => {
    return history.slice(0, limit);
  }, [history]);

  return {
    history,
    addToHistory,
    removeFromHistory,
    clearHistory,
    getRecentHistory
  };
}
